import { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Button,
  Tabs,
  Tab,
  Box,
  Typography,
  Stack,
  Paper,
  Divider,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';

import SignatureCanvas from './components/SignatureCanvas';
import SignatureTyper from './components/SignatureTyper';
import SignatureUploader from './components/SignatureUploader';
import SignaturePreview from './components/SignaturePreview';
import SavedSignatures from './components/SavedSignatures';
import { SIGNATURE_FONTS } from './components/signatureConstants';
import useSignatureStore from './hooks/useSignatureStore';

import './signature.scss'

export const SignatureCreator = ({
  width = 600,
  height = 200,
  initialSignature = null,
  isOpen = false,
  onClose = () => { },
  onSign = () => { },
  onSignAndSave = () => { },
  signaturePath = '',
  formik = null,
  path = '',
  disableSignAndSave = true, // New prop with default false (button shown by default)
}) => {
  const [tabValue, setTabValue] = useState(0);
  const [signatureData, setSignatureData] = useState(initialSignature);
  const { savedSignatures, saveSignature, deleteSavedSignature } = useSignatureStore();

  useEffect(() => {
    SIGNATURE_FONTS.forEach((font) => {
      const link = document.createElement('link');
      link.href = font.url;
      link.rel = 'stylesheet';
      document.head.appendChild(link);

      return () => {
        document.head.removeChild(link);
      };
    });
  }, []);

  const handleCapture = (data) => {
    setSignatureData(data);
  };

  const handleTabChange = (_e, newValue) => {
    setTabValue(newValue);
    setSignatureData(null);
  };

  const setFormikValue = (value) => {
    if (formik && path) {
      const keys = path.split('.');
      const lastKey = keys.pop();
      const target = keys.reduce((obj, key) => obj?.[key], formik.values);
      if (target && lastKey) target[lastKey] = value;
      formik.setValues({ ...formik.values });
    }
  };

  const handleSign = () => {
    if (signatureData) {
      onSign({ signatureData, signaturePath });
      setFormikValue(signatureData);
      onClose();
    }
  };

  const handleSignAndSave = () => {
    if (signatureData) {
      const newSignature = {
        id: Date.now().toString(),
        name: 'Signature ' + new Date().toLocaleDateString(),
        data: signatureData,
        createdAt: new Date().toISOString(),
        path: signaturePath,
      };

      // Use the new saveSignature method instead of setSavedSignatures
      saveSignature(newSignature);

      onSignAndSave({
        signatureData,
        signaturePath,
        signatureId: newSignature.id,
      });
      setFormikValue(signatureData);
      onClose();
    }
  };

  const selectSavedSignature = (sigData) => {
    setSignatureData(sigData);
  };

  const hasActiveSignature = Boolean(signatureData);

  return (
    <Dialog open={isOpen} onClose={onClose} fullWidth maxWidth="lg" className='signature-dialog'>
      <DialogTitle sx={{ m: 0, p: 2 }}>
        <Typography variant="h6">Create Your Signature</Typography>
        <IconButton
          aria-label="close"
          onClick={onClose}
          sx={{ position: 'absolute', right: 12, top: 12 }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers sx={{ px: 3, py: 2, height: disableSignAndSave ? '64vh' : '75vh', overflowY: 'auto' }}>
        <Stack spacing={3}>
          {/* Tabs */}
          <Box>
            <Tabs
              value={tabValue}
              onChange={handleTabChange}
              textColor="primary"
              indicatorColor="primary"
              variant="fullWidth"
            >
              <Tab label="Type" />
              <Tab label="Draw" />
              <Tab label="Upload" />
            </Tabs>
            <Box mt={2}>
              {tabValue === 0 && (
                <SignatureTyper width={width} height={height} onCapture={handleCapture} disableSignAndSave={disableSignAndSave} />
              )}
              {tabValue === 1 && (
                <SignatureCanvas width={width} height={height} onCapture={handleCapture} />
              )}
              {tabValue === 2 && (
                <SignatureUploader width={width} height={height} onCapture={handleCapture} />
              )}
            </Box>
          </Box>

          {/* Preview */}
          {signatureData && (
            <Box>
              <Typography variant="subtitle1" gutterBottom>
                Preview
              </Typography>
              <Paper variant="outlined" sx={{ p: 2 }}>
                <SignaturePreview signatureData={signatureData} />
              </Paper>
            </Box>
          )}

          {/* Saved Signatures */}
          {!disableSignAndSave && savedSignatures.length > 0 && (
            <Box>
              <Divider sx={{ mb: 2 }} />
              <Typography variant="subtitle1" gutterBottom>
                Saved Signatures
              </Typography>
              <SavedSignatures
                signatures={savedSignatures}
                onSelect={selectSavedSignature}
                onDelete={deleteSavedSignature}
              />
            </Box>
          )}
        </Stack>
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose} variant="outlined">
          Cancel
        </Button>
        <Button
          onClick={handleSign}
          disabled={!hasActiveSignature}
          variant="contained"
          color="primary"
        >
          Sign
        </Button>
        {!disableSignAndSave && (
          <Button
            onClick={handleSignAndSave}
            disabled={!hasActiveSignature}
            variant="contained"
            color="success"
          >
            Sign & Save
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};